import React from 'react';
import {
  PDGM_OUTSIDE_EPISODE_DAY_BG,
  PDGM_PERIOD_1_DAY_BG,
} from '../utils/pdgmPeriodUtils';
import {
  ROGER_CHARTING_BAR_CLASS,
  START_OF_ROGER_CHARTING_LEGEND,
} from './patient/chartsFromEpisodeUi';
import {
  COMPLETED_VISIT_SWATCH_STYLE,
  SCHEDULED_VISIT_SWATCH_STYLE,
  TODAY_SWATCH_STYLE,
} from './useEpisodeCalendarMarkers';

export const EpisodeCalendarLegendItem: React.FC<{
  swatch: React.ReactNode;
  children: React.ReactNode;
}> = ({ swatch, children }) => (
  <div className='flex items-center gap-1.5'>
    {swatch}
    <span>{children}</span>
  </div>
);

interface EpisodeCalendarLegendProps {
  hasTwoPdgmPeriods: boolean;
  showChartsFromCutoff: boolean;
  className?: string;
  /** Keys for markers only this calendar draws, listed first. */
  children?: React.ReactNode;
}

/** Key for the markers {@link useEpisodeCalendarMarkers} draws. */
const EpisodeCalendarLegend: React.FC<EpisodeCalendarLegendProps> = ({
  hasTwoPdgmPeriods,
  showChartsFromCutoff,
  className = '',
  children,
}) => (
  <div
    className={`mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-700 ${className}`}
  >
    {children}
    <EpisodeCalendarLegendItem
      swatch={
        <div className='w-3 h-3 rounded-sm border border-gray-300 bg-white' />
      }
    >
      Episode Day
    </EpisodeCalendarLegendItem>
    <EpisodeCalendarLegendItem
      swatch={
        <div
          className='w-3 h-3 rounded-sm'
          style={SCHEDULED_VISIT_SWATCH_STYLE}
        />
      }
    >
      Scheduled Visit
    </EpisodeCalendarLegendItem>
    <EpisodeCalendarLegendItem
      swatch={
        <div
          className='w-3 h-3 rounded-sm'
          style={COMPLETED_VISIT_SWATCH_STYLE}
        />
      }
    >
      Completed Visit
    </EpisodeCalendarLegendItem>
    <EpisodeCalendarLegendItem
      swatch={
        <div
          className='h-3 w-3 rounded-sm border border-slate-300'
          style={{ backgroundColor: PDGM_OUTSIDE_EPISODE_DAY_BG }}
        />
      }
    >
      Outside Episode
    </EpisodeCalendarLegendItem>
    <EpisodeCalendarLegendItem
      swatch={<div className='w-3 h-3 rounded-sm' style={TODAY_SWATCH_STYLE} />}
    >
      Today
    </EpisodeCalendarLegendItem>
    {showChartsFromCutoff && (
      <>
        <EpisodeCalendarLegendItem
          swatch={
            <div
              className='external-emr-hatch h-3 w-3 rounded-sm border border-slate-300 bg-white'
              aria-hidden
            />
          }
        >
          External EMR charting
        </EpisodeCalendarLegendItem>
        <EpisodeCalendarLegendItem
          swatch={
            <div className='relative flex h-4 w-3 items-center justify-center rounded-sm bg-white'>
              <div
                className={`absolute h-full w-[3px] rounded-full ${ROGER_CHARTING_BAR_CLASS}`}
              />
            </div>
          }
        >
          {START_OF_ROGER_CHARTING_LEGEND}
        </EpisodeCalendarLegendItem>
      </>
    )}
    {hasTwoPdgmPeriods && (
      <>
        <EpisodeCalendarLegendItem
          swatch={
            <div
              className='h-3 w-3 rounded-sm border border-slate-200'
              style={{ backgroundColor: PDGM_PERIOD_1_DAY_BG }}
              aria-hidden
            />
          }
        >
          1st 30-day period
        </EpisodeCalendarLegendItem>
        <EpisodeCalendarLegendItem
          swatch={
            <div
              className='h-3 w-3 rounded-sm border border-gray-300 bg-white'
              aria-hidden
            />
          }
        >
          2nd 30-day period
        </EpisodeCalendarLegendItem>
        <EpisodeCalendarLegendItem
          swatch={
            <div className='relative flex h-4 w-3 items-center justify-center rounded-sm bg-white'>
              <div className='absolute h-full w-[3px] rounded-full bg-gray-900' />
            </div>
          }
        >
          30-day period boundary
        </EpisodeCalendarLegendItem>
      </>
    )}
  </div>
);

export default EpisodeCalendarLegend;
